import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Text } from "@mantine/core";
import { IconCircleCheckFilled } from "@tabler/icons-react";

type LoadingState = {
  text: string;
};

interface MultiStepLoaderProps {
  loadingStates: LoadingState[];
  loading?: boolean;
  duration?: number;
  generationKey?: string | number;
  title?: string;
  subtitle?: string;
  inline?: boolean;
  minimal?: boolean;
  textSize?: string;
  countdownSeconds?: number;
  overtimeMessage?: string;
}

export function MultiStepLoader({
  loadingStates,
  loading,
  duration = 2000,
  generationKey,
  title = "GTsetu AI is generating your profile",
  subtitle = "Please wait a moment. It may take some time to generate your profile.",
  inline = false,
  minimal = false,
  textSize = "sm",
  countdownSeconds,
  overtimeMessage = "It is taking more time than expected.",
}: MultiStepLoaderProps) {
  const [currentState, setCurrentState] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState(countdownSeconds ?? 0);

  const showCountdown = typeof countdownSeconds === "number" && countdownSeconds > 0 && !minimal;
  const isOvertime = showCountdown && remainingSeconds === 0;
  const isInlineMinimal = inline && minimal;

  // Advance step on interval, looping back to start
  useEffect(() => {
    if (!loading) return;
    const timeout = setTimeout(() => {
      setCurrentState((prev) => (prev < loadingStates.length - 1 ? prev + 1 : prev));
    }, duration);
    return () => clearTimeout(timeout);
  }, [currentState, loading, loadingStates.length, duration]);

  // Reset when a new generation starts
  useEffect(() => {
    setCurrentState(0);
    if (showCountdown) {
      setRemainingSeconds(countdownSeconds);
    }
  }, [generationKey]);

  useEffect(() => {
    if (!showCountdown || !loading) return;
    if (remainingSeconds <= 0) return;

    const timer = setInterval(() => {
      setRemainingSeconds((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [loading, remainingSeconds, showCountdown]);

  const formattedTime = `${Math.floor(remainingSeconds / 60)}:${String(remainingSeconds % 60).padStart(2, "0")}`;

  return (
    <AnimatePresence mode="wait">
      {loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: inline ? "absolute" : "fixed",
            inset: 0,
            zIndex: 200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "clamp(16px, 4vw, 40px)",
            boxSizing: "border-box",
            backgroundColor: isInlineMinimal ? "transparent" : "rgba(255, 255, 255, 0.75)",
            backdropFilter: isInlineMinimal ? "none" : "blur(6px)",
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            style={
              minimal
                ? {
                  background: inline ? "rgba(240, 244, 252, 0.92)" : "transparent",
                  border: inline ? "1px solid #E5E7EB" : "none",
                  borderRadius: 12,
                  padding: inline ? "14px 16px" : 0,
                  width: "100%",
                  maxWidth: inline ? "min(420px, 100%)" : "none",
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }
                : {
                  background: "#f0f4fc",
                  borderRadius: 16,
                  padding: "clamp(24px, 3vw, 36px) clamp(28px, 3.5vw, 40px)",
                  width: "100%",
                  maxWidth: "min(420px, 100%)",
                  border: "1px solid #E5E7EB",
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }
            }
          >
            {!minimal && (
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
                <img src="/gtLogo.svg" alt="GTsetu" style={{ height: 96, width: "auto" }} />
              </div>
            )}

            {title && (
              <Text fz={22} fw={600} mb={8} c="slate.10" lh={1.25}>
                {title}
              </Text>
            )}
            {subtitle && (
              <Text fz={15} c="slate.7" mb={16} lh={1.45}>
                {subtitle}
              </Text>
            )}
            <Text fz={12} c="slate.6" mb={14}>
              Do not refresh or close this page
            </Text>
            {showCountdown && (
              <Text fz={13} fw={600} c={isOvertime ? "orange.8" : "slate.8"} mb={10}>
                Time remaining: {formattedTime}
              </Text>
            )}
            {isOvertime && (
              <Text fz={13} fw={600} c="orange.8" mb={14}>
                {overtimeMessage}
              </Text>
            )}

            <div style={{ width: "100%", maxWidth: 360 }}>
              {loadingStates.map((state, index) => {
                const isCompleted = index < currentState;
                const isCurrent = index === currentState;
                const isVisible = index <= currentState;

                if (!isVisible) return null;

                return (
                  <motion.div
                    key={`${generationKey ?? "gen"}-${index}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      marginBottom: 6,
                    }}
                  >
                    <IconCircleCheckFilled
                      size={18}
                      color={isCompleted ? "#16A34A" : isCurrent ? "#94A3B8" : "#CBD5E1"}
                    />
                    <Text fz={textSize === "sm" ? 16 : textSize} fw={isCurrent ? 600 : 500} c={isCurrent ? "slate.9" : "slate.7"}>
                      {state.text}
                    </Text>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
